import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRecordAttemptMutation } from "@/hooks/useUserProgress";
import { useTask } from "./TaskContext";
import { toast } from "sonner";
import type { Task } from "./TaskContext";
import { useQueryClient } from "@tanstack/react-query";
import { examSessionKeys } from "@/hooks/useExamSessions";

// Available exam document
export interface ExamDocument {
  documentType: string;
  year: number;
  taskCount: number;
}

// Answer record for a task
export interface ExamAnswer {
  taskId: string;
  taskNumber: number;
  subpoint?: number;
  taskType: string;
  selectedAnswer?: number | number[]; // For ABCD, multi-select
  trueFalseAnswers?: boolean[]; // For P/F tasks
  matchingAnswers?: Record<string, string>; // For AB/123
  tableAnswers?: Record<string, string>; // For table tasks
  openAnswer?: string; // For open tasks
  verdictAnswer?: number; // For open tasks with verdict options
  strikethroughSelections?: number[]; // For Podkreślanka tasks
  isAutoGraded: boolean;
  isCorrect?: boolean; // null for open tasks awaiting grading
  earnedPoints?: number;
  maxPoints: number;
}

// Challenge data when taking someone's challenge
export interface ChallengeData {
  id: string;
  challengerName: string;
  documentType: string;
  year: number | null;
  taskIds: string[];
  challengerScore: number;
  challengerMaxScore: number;
  challengerPercentage: number;
  challengerTimeFormatted: string;
}

// Custom exam data
export interface CustomExamData {
  homeworkId: string;
  timeLimitMinutes: number;
}

interface ExamModeContextType {
  // State
  isExamMode: boolean;
  selectedDocument: ExamDocument | null;
  availableDocuments: ExamDocument[];
  examTasks: Task[];
  currentTaskIndex: number;
  currentTask: Task | null;
  answers: Map<string, ExamAnswer>;
  skippedTaskIds: string[];
  timeRemaining: number; // seconds
  isTimerRunning: boolean;
  isTimerVisible: boolean;
  isExamFinished: boolean;
  isLoadingExam: boolean;
  isLoadingDocuments: boolean;
  challengeData: ChallengeData | null;
  pendingChallengeId: string | null;
  customExamData: CustomExamData | null;
  
  // Computed
  completedCount: number;
  totalCount: number;
  progressPercentage: number;
  autoGradedResults: { correct: number; total: number; points: number; maxPoints: number };
  allTasksComplete: boolean;
  
  // Actions
  setIsExamMode: (value: boolean) => void;
  loadAvailableDocuments: () => Promise<void>;
  selectDocument: (doc: ExamDocument) => Promise<void>;
  startChallengeExam: (tasks: Task[], doc: ExamDocument, data: ChallengeData) => Promise<void>;
  startCustomExam: (tasks: Task[], timeLimitMinutes: number, homeworkId: string) => Promise<void>;
  goToTask: (index: number) => void;
  goToNextTask: () => void;
  skipCurrentTask: () => void;
  recordAnswer: (answer: Omit<ExamAnswer, 'taskId' | 'taskNumber' | 'subpoint'>, taskId?: string) => void;
  getAnswer: (taskId: string) => ExamAnswer | undefined;
  toggleTimer: () => void;
  toggleTimerRunning: () => void;
  finishExam: () => void;
  resetExam: (forceReset?: boolean) => void;
  setPendingChallengeId: (id: string | null) => void;
}

export const ExamModeContext = createContext<ExamModeContextType | undefined>(undefined);

const EXAM_DURATION_MINUTES = 180;
const EXAM_DURATION_SECONDS = EXAM_DURATION_MINUTES * 60;
const EXAM_STATE_KEY = "biozadania_exam_state";
const CUSTOM_EXAM_STATE_KEY = "biozadania_custom_exam_state";

// Saved exam state interface for localStorage
interface SavedExamState {
  selectedDocument: ExamDocument | null;
  examTasks: Task[];
  currentTaskIndex: number;
  answers: [string, ExamAnswer][]; // Map serialized as entries
  skippedTaskIds: string[];
  timeRemaining: number;
  isExamFinished: boolean;
  savedAt: number; // timestamp
  sessionId: string | null; // DB session ID
  pendingChallengeId?: string | null;
  customExamData?: CustomExamData | null;
  initialTimeSeconds?: number;
}

const PENDING_CHALLENGE_KEY = "biozadania_pending_challenge_id";

// Helper to map DB task to Task interface
const mapDbTaskToTask = (dbTask: any): Task => ({
  id: dbTask.id,
  slug: dbTask.slug || undefined,
  question: dbTask.question,
  instruction: dbTask.instruction || undefined,
  sources: dbTask.sources || undefined,
  answers: dbTask.answers,
  correctIndex: dbTask.correct_index,
  categories: dbTask.categories || (dbTask.category ? [dbTask.category] : []),
  year: dbTask.year || undefined,
  documentType: dbTask.document_type || undefined,
  formula: dbTask.formula || undefined,
  taskNumber: dbTask.task_number || undefined,
  subpoint: dbTask.subpoint || undefined,
  imageUrls: dbTask.image_urls || (dbTask.image_url ? [dbTask.image_url] : []),
  imageCompressed: dbTask.image_compressed || undefined,
  taskType: dbTask.task_type || undefined,
  taskSubtype: dbTask.task_subtype || undefined,
  useYesNo: dbTask.use_yes_no || false,
  modelAnswer: dbTask.model_answer || undefined,
  prefilledAnswer: dbTask.prefilled_answer || undefined,
  operationalVerb: dbTask.operational_verb || undefined,
  tableData: dbTask.table_data || undefined,
  matchingData: dbTask.matching_data || undefined,
  strikethroughText: dbTask.strikethrough_text || undefined,
  isTwoPoints: dbTask.is_two_points || false,
  maxPoints: (dbTask.multi_table_data && Array.isArray(dbTask.multi_table_data) && dbTask.multi_table_data.length > 1)
    ? (dbTask.multi_table_data as any[]).reduce((sum: number, entry: any) => sum + (entry.maxPoints || 1), 0)
    : (dbTask.max_points || (dbTask.is_two_points ? 2 : 1)),
  isMultiSelect: dbTask.is_multi_select || false,
  tableScoringMode: dbTask.table_scoring_mode || null,
  customScoringScenarios: dbTask.custom_scoring_scenarios || undefined,
  verdictOptions: dbTask.verdict_options || undefined,
  multiTableData: dbTask.multi_table_data || undefined,
});

export const ExamModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const recordAttemptMutation = useRecordAttemptMutation();
  const { markTasksCompleted } = useTask();
  const queryClient = useQueryClient();
  
  // Core state
  const [isExamMode, setIsExamMode] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<ExamDocument | null>(null);
  const [availableDocuments, setAvailableDocuments] = useState<ExamDocument[]>([]);
  const [examTasks, setExamTasks] = useState<Task[]>([]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<string, ExamAnswer>>(new Map());
  const [skippedTaskIds, setSkippedTaskIds] = useState<string[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(EXAM_DURATION_SECONDS);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isTimerVisible, setIsTimerVisible] = useState(true);
  const [isExamFinished, setIsExamFinished] = useState(false);
  const [isLoadingExam, setIsLoadingExam] = useState(false);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isRestoringState, setIsRestoringState] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [challengeData, setChallengeData] = useState<ChallengeData | null>(null);
  const [customExamData, setCustomExamData] = useState<CustomExamData | null>(null);
  const [initialTimeSeconds, setInitialTimeSeconds] = useState(EXAM_DURATION_SECONDS);
  const [pendingChallengeId, setPendingChallengeIdState] = useState<string | null>(() => {
    try { return localStorage.getItem(PENDING_CHALLENGE_KEY); } catch { return null; }
  });
  
  const setPendingChallengeId = useCallback((id: string | null) => {
    setPendingChallengeIdState(id);
    try {
      if (id) localStorage.setItem(PENDING_CHALLENGE_KEY, id);
      else localStorage.removeItem(PENDING_CHALLENGE_KEY);
    } catch {}
  }, []);
  
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRestoredRef = useRef(false);
  
  // Save exam state to localStorage
  const saveExamState = useCallback(() => {
    if (!selectedDocument || examTasks.length === 0) return;
    
    const stateKey = customExamData ? CUSTOM_EXAM_STATE_KEY : EXAM_STATE_KEY;
    const state: SavedExamState = {
      selectedDocument,
      examTasks,
      currentTaskIndex,
      answers: Array.from(answers.entries()),
      skippedTaskIds,
      timeRemaining,
      isExamFinished,
      savedAt: Date.now(),
      sessionId: currentSessionId,
      customExamData,
      initialTimeSeconds,
    };
    
    try {
      localStorage.setItem(stateKey, JSON.stringify(state));
    } catch (error) {
      console.error("Error saving exam state:", error);
    }
  }, [selectedDocument, examTasks, currentTaskIndex, answers, skippedTaskIds, timeRemaining, isExamFinished, currentSessionId, customExamData, initialTimeSeconds]);
  
  // Clear saved exam state
  const clearSavedExamState = useCallback(() => {
    try {
      localStorage.removeItem(EXAM_STATE_KEY);
      localStorage.removeItem(CUSTOM_EXAM_STATE_KEY);
    } catch (error) {
      console.error("Error clearing exam state:", error);
    }
  }, []);
  
  // Try to restore exam state from localStorage
  const tryRestoreExamState = useCallback((): boolean => {
    try {
      // Check both keys - standard and custom exam
      let savedState = localStorage.getItem(EXAM_STATE_KEY);
      if (!savedState) {
        savedState = localStorage.getItem(CUSTOM_EXAM_STATE_KEY);
      }
      if (!savedState) {
        return false;
      }
      
      const state: SavedExamState = JSON.parse(savedState);
      
      // Check if saved state is older than 24 hours
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      if (Date.now() - state.savedAt > maxAge) {
        clearSavedExamState();
        return false;
      }
      
      // Restore state
      setSelectedDocument(state.selectedDocument);
      setExamTasks(state.examTasks);
      setCurrentTaskIndex(state.currentTaskIndex);
      setAnswers(new Map(state.answers));
      setSkippedTaskIds(state.skippedTaskIds);
      setTimeRemaining(state.timeRemaining);
      setIsExamFinished(state.isExamFinished);
      setCurrentSessionId(state.sessionId);
      
      // Restore custom exam data and initial time
      if (state.customExamData) {
        setCustomExamData(state.customExamData);
        warningShownRef.current = false;
      }
      setInitialTimeSeconds(state.initialTimeSeconds ?? EXAM_DURATION_SECONDS);
      
      // Auto-start timer when restoring session (if exam not finished)
      if (!state.isExamFinished) {
        setIsTimerRunning(true);
      }
      
      // Verify task count against database and refresh if changed
      if (state.selectedDocument) {
        const doc = state.selectedDocument;
        supabase
          .from("tasks")
          .select("id", { count: "exact", head: true })
          .eq("document_type", doc.documentType)
          .eq("year", doc.year)
          .then(({ count }) => {
            if (count !== null && count !== state.examTasks.length) {
              console.log(`Exam task count changed: ${state.examTasks.length} → ${count}. Refreshing...`);
              // Fetch fresh tasks
              supabase
                .from("tasks")
                .select("*")
                .eq("document_type", doc.documentType)
                .eq("year", doc.year)
                .order("task_number", { ascending: true })
                .order("subpoint", { ascending: true, nullsFirst: true })
                .then(({ data, error }) => {
                  if (!error && data) {
                    const freshTasks = data.map(mapDbTaskToTask);
                    setExamTasks(freshTasks);
                    // Update document with correct count
                    setSelectedDocument({ ...doc, taskCount: freshTasks.length });
                    toast.info("Lista zadań została zaktualizowana", {
                      description: `Dodano nowe zadania do arkusza (${state.examTasks.length} → ${freshTasks.length})`,
                      duration: 4000,
                    });
                  }
                });
            }
          });
      }
      
      // Show restoration toast
      const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        return `${mins} min`;
      };
      
      toast.success("Przywrócono poprzednią sesję egzaminu", {
        description: `${state.selectedDocument?.documentType} ${state.selectedDocument?.year} • Pozostało ${formatTime(state.timeRemaining)}`,
        duration: 5000,
      });
      
      console.log("Exam state restored from localStorage");
      return true;
    } catch (error) {
      console.error("Error restoring exam state:", error);
      clearSavedExamState();
      return false;
    }
  }, [clearSavedExamState]);
  
  // Restore exam state from localStorage on mount
  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;
    
    const restored = tryRestoreExamState();
    if (restored) {
      setIsExamMode(true);
    }
    setIsRestoringState(false);
  }, [tryRestoreExamState]);
  
  // Restore session when returning to exam mode (not on initial mount)
  const prevIsExamModeRef = useRef<boolean | null>(null);
  useEffect(() => {
    // Skip initial render
    if (prevIsExamModeRef.current === null) {
      prevIsExamModeRef.current = isExamMode;
      return;
    }
    
    // Detect transition from false -> true (returning to exam mode)
    if (!prevIsExamModeRef.current && isExamMode) {
      // Try to restore saved session
      tryRestoreExamState();
    }
    
    prevIsExamModeRef.current = isExamMode;
  }, [isExamMode, tryRestoreExamState]);
  
  // Auto-save exam state when it changes
  useEffect(() => {
    if (isRestoringState) return;
    if (!isExamMode || !selectedDocument || examTasks.length === 0) return;
    
    saveExamState();
  }, [isExamMode, selectedDocument, examTasks, currentTaskIndex, answers, skippedTaskIds, timeRemaining, isExamFinished, saveExamState, isRestoringState]);
  
  // Current task in sequence
  const currentTask = useMemo(() => {
    if (examTasks.length === 0 || currentTaskIndex >= examTasks.length) return null;
    return examTasks[currentTaskIndex];
  }, [examTasks, currentTaskIndex]);
  
  // Computed values
  const completedCount = useMemo(() => answers.size, [answers]);
  const totalCount = examTasks.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  
  // Auto-graded results (ABCD, P/F, AB/123, tables)
  const autoGradedResults = useMemo(() => {
    const autoGraded = Array.from(answers.values()).filter(a => a.isAutoGraded);
    const correct = autoGraded.filter(a => a.isCorrect).length;
    const points = autoGraded.reduce((sum, a) => sum + (a.earnedPoints || 0), 0);
    const maxPoints = autoGraded.reduce((sum, a) => sum + a.maxPoints, 0);
    return { correct, total: autoGraded.length, points, maxPoints };
  }, [answers]);
  
  // Timer effect with 60s warning for custom exams
  const warningShownRef = useRef(false);
  useEffect(() => {
    if (isTimerRunning && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          // 60s warning for custom exams
          if (prev === 61 && customExamData && !warningShownRef.current) {
            warningShownRef.current = true;
            toast.warning("⏰ Pozostała 1 minuta!", {
              description: "Po upływie czasu arkusz zostanie automatycznie zakończony.",
              duration: 10000,
            });
          }
          if (prev <= 1) {
            setIsTimerRunning(false);
            setIsExamFinished(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isTimerRunning, customExamData]);
  
  // Load available exam documents
  const loadAvailableDocuments = useCallback(async () => {
    setIsLoadingDocuments(true);
    try {
      const { data, error } = await supabase
        .from("tasks")
        .select("document_type, year")
        .not("is_premium", "is", true)
        .not("document_type", "is", null)
        .not("year", "is", null);
      
      if (error) {
        console.error("Error loading documents:", error);
        return;
      }
      
      // Group by document_type + year and count tasks
      const docMap = new Map<string, { documentType: string; year: number; count: number }>();
      
      data.forEach(task => {
        const key = `${task.document_type}|${task.year}`;
        const existing = docMap.get(key);
        if (existing) {
          existing.count++;
        } else {
          docMap.set(key, {
            documentType: task.document_type!,
            year: task.year!,
            count: 1
          });
        }
      });
      
      const docs: ExamDocument[] = Array.from(docMap.values())
        .filter(d => !d.documentType.startsWith("Informator") && d.documentType !== "Matura Próbna")
        .map(d => ({
          documentType: d.documentType,
          year: d.year,
          taskCount: d.count
        }))
        .sort((a, b) => {
          // Sort by year desc, then by document type
          if (a.year !== b.year) return b.year - a.year;
          return a.documentType.localeCompare(b.documentType);
        });
      
      setAvailableDocuments(docs);
    } finally {
      setIsLoadingDocuments(false);
    }
  }, []);
  
  // Select a document and load its tasks
  const selectDocument = useCallback(async (doc: ExamDocument) => {
    setIsLoadingExam(true);
    setSelectedDocument(doc);
    
    // Fetch tasks for this document, ordered by task number and subpoint
    const { data, error } = await supabase
      .from("tasks")
      .select("*")
      .eq("document_type", doc.documentType)
      .eq("year", doc.year)
      .not("is_premium", "is", true)
      .order("task_number", { ascending: true })
      .order("subpoint", { ascending: true, nullsFirst: true });
    
    if (error) {
      console.error("Error loading exam tasks:", error);
      setIsLoadingExam(false);
      return;
    }
    
    const tasks = data.map(mapDbTaskToTask);
    
    // Create exam session in database for authenticated users
    let sessionId: string | null = null;
    if (user) {
      try {
        const { data: session, error: sessionError } = await supabase
          .from("exam_sessions")
          .insert({
            user_id: user.id,
            document_type: doc.documentType,
            year: doc.year,
            started_at: new Date().toISOString(),
            is_completed: false,
          })
          .select()
          .single();
        
        if (sessionError) {
          console.error("Error creating exam session:", sessionError);
        } else {
          sessionId = session.id;
        }
      } catch (err) {
        console.error("Error creating exam session:", err);
      }
    }
    
    setCurrentSessionId(sessionId);
    setExamTasks(tasks);
    setCurrentTaskIndex(0);
    setAnswers(new Map());
    setSkippedTaskIds([]);
    setTimeRemaining(EXAM_DURATION_SECONDS);
    setInitialTimeSeconds(EXAM_DURATION_SECONDS);
    setIsTimerRunning(true);
    setIsExamFinished(false);
    setIsLoadingExam(false);
  }, [user]);

  // Navigate to specific task
  const goToTask = useCallback((index: number) => {
    if (index >= 0 && index < examTasks.length) {
      setCurrentTaskIndex(index);
    }
  }, [examTasks.length]);
  
  // Find next unanswered or skipped task index
  const findNextIncompleteTask = useCallback((): number | null => {
    // First, look for any unanswered task (not in answers map)
    for (let i = 0; i < examTasks.length; i++) {
      const task = examTasks[i];
      if (!answers.has(task.id)) {
        return i;
      }
    }
    // All tasks answered - no incomplete tasks
    return null;
  }, [examTasks, answers]);

  // Check if all tasks are complete
  const allTasksComplete = useMemo(() => {
    return examTasks.length > 0 && answers.size >= examTasks.length;
  }, [examTasks.length, answers.size]);

  // Go to next task
  const goToNextTask = useCallback(() => {
    // If we're not at the end, just go to next
    if (currentTaskIndex < examTasks.length - 1) {
      setCurrentTaskIndex(prev => prev + 1);
      return;
    }
    
    // We're at the last task - find first incomplete task
    const nextIncomplete = findNextIncompleteTask();
    if (nextIncomplete !== null && nextIncomplete !== currentTaskIndex) {
      setCurrentTaskIndex(nextIncomplete);
      return;
    }
    
    // Check for skipped tasks that may not be answered yet
    for (const skippedId of skippedTaskIds) {
      if (!answers.has(skippedId)) {
        const skippedIndex = examTasks.findIndex(t => t.id === skippedId);
        if (skippedIndex !== -1) {
          setCurrentTaskIndex(skippedIndex);
          setSkippedTaskIds(prev => prev.filter(id => id !== skippedId));
          return;
        }
      }
    }
    
    // All tasks complete - stay on current or wrap to first
    // (The UI will show "Zakończ arkusz" button when allTasksComplete is true)
  }, [currentTaskIndex, examTasks, skippedTaskIds, answers, findNextIncompleteTask]);
  
  // Skip current task (goes to end of queue)
  const skipCurrentTask = useCallback(() => {
    if (!currentTask) return;
    
    // Only add to skipped if not already answered
    if (!answers.has(currentTask.id)) {
      setSkippedTaskIds(prev => [...prev, currentTask.id]);
    }
    
    goToNextTask();
  }, [currentTask, answers, goToNextTask]);
  
  // Record an answer for current task (or specific task by ID)
  const recordAnswer = useCallback((answer: Omit<ExamAnswer, 'taskId' | 'taskNumber' | 'subpoint'>, taskId?: string) => {
    const targetTask = taskId ? examTasks.find(t => t.id === taskId) : currentTask;
    if (!targetTask) return;
    
    const fullAnswer: ExamAnswer = {
      ...answer,
      taskId: targetTask.id,
      taskNumber: targetTask.taskNumber || 0,
      subpoint: targetTask.subpoint,
    };
    
    setAnswers(prev => new Map(prev).set(targetTask.id, fullAnswer));
    
    // Remove from skipped if it was there
    setSkippedTaskIds(prev => prev.filter(id => id !== targetTask.id));
  }, [currentTask, examTasks]);
  
  // Get answer for a specific task
  const getAnswer = useCallback((taskId: string): ExamAnswer | undefined => {
    return answers.get(taskId);
  }, [answers]);
  
  // Toggle timer visibility
  const toggleTimer = useCallback(() => {
    setIsTimerVisible(prev => !prev);
  }, []);

  // Toggle timer running state (pause/resume) - blocked for custom exams
  const toggleTimerRunning = useCallback(() => {
    if (customExamData) return; // Custom exams cannot be paused
    setIsTimerRunning(prev => !prev);
  }, [customExamData]);

  // Finish exam and save results
  const finishExam = useCallback(async () => {
    setIsTimerRunning(false);
    setIsExamFinished(true);
    
    // Clear saved state - exam is complete
    clearSavedExamState();
    
    // Collect all completed task IDs for local state update
    const completedTaskIds: string[] = [];
    
    // Calculate time spent
    const timeSpentSeconds = initialTimeSeconds - timeRemaining;
    
    // Record all auto-graded answers to user progress and save exam session
    if (user && currentSessionId) {
      // Calculate totals for session
      let totalAutoPoints = 0;
      let maxAutoPoints = 0;
      let maxOpenPoints = 0;
      
      // First, save open task responses to database and get their IDs
      const openTaskResponseIds: Record<string, string> = {};
      
      for (const task of examTasks) {
        const answer = answers.get(task.id);
        // Check for open task - taskSubtype is 'zadanie otwarte' or task type is 'otwarte'
        const isOpenTask = task.taskSubtype === 'zadanie otwarte' || task.taskType === 'otwarte';
        
        if (isOpenTask && answer?.openAnswer) {
          try {
            // ALWAYS create a new response for each exam session - never reuse existing!
            // This ensures each session has its own grading status independent of others
            const { data: newResponse, error: insertErr } = await supabase
              .from("open_task_responses")
              .insert({
                user_id: user.id,
                task_id: task.id,
                response_text: answer.openAnswer,
                exam_session_id: currentSessionId,
              })
              .select("id")
              .single();
            
            if (!insertErr && newResponse) {
              openTaskResponseIds[task.id] = newResponse.id;
            } else {
              console.error("Error creating open task response:", insertErr);
            }
          } catch (err) {
            console.error("Error saving open task response:", err);
          }
        }
      }
      
      // Prepare task records for database
      const taskRecords: {
        session_id: string;
        task_id: string;
        task_number: number | null;
        subpoint: number | null;
        task_type: string;
        user_answer: ExamAnswer | null;
        is_correct: boolean | null;
        points_earned: number;
        max_points: number;
        open_task_response_id: string | null;
      }[] = [];
      
      for (const task of examTasks) {
        const answer = answers.get(task.id);
        const maxPoints = task.maxPoints || (task.isTwoPoints ? 2 : 1);
        // Check for open task - taskSubtype is 'zadanie otwarte' or task type is 'otwarte'
        const isOpenTask = task.taskSubtype === 'zadanie otwarte' || task.taskType === 'otwarte';
        
        if (isOpenTask) {
          maxOpenPoints += maxPoints;
        } else {
          maxAutoPoints += maxPoints;
          if (answer?.earnedPoints !== undefined) {
            totalAutoPoints += answer.earnedPoints;
          }
        }
        
        taskRecords.push({
          session_id: currentSessionId,
          task_id: task.id,
          task_number: task.taskNumber || null,
          subpoint: task.subpoint || null,
          task_type: isOpenTask ? 'open' : 'auto',
          user_answer: answer || null,
          is_correct: answer?.isCorrect ?? null,
          points_earned: answer?.earnedPoints || 0,
          max_points: maxPoints,
          open_task_response_id: openTaskResponseIds[task.id] || null,
        });
      }
      
      // Insert all task records
      try {
        const { error: tasksError } = await supabase
          .from("exam_session_tasks")
          .insert(taskRecords as any[]);
        
        if (tasksError) {
          console.error("Error saving exam session tasks:", tasksError);
        }
        
        // Update session as completed
        const { error: updateError } = await supabase
          .from("exam_sessions")
          .update({
            is_completed: true,
            completed_at: new Date().toISOString(),
            time_spent_seconds: timeSpentSeconds,
            total_auto_points: totalAutoPoints,
            max_auto_points: maxAutoPoints,
            max_open_points: maxOpenPoints,
            updated_at: new Date().toISOString(),
          })
          .eq("id", currentSessionId);
        
        if (updateError) {
          console.error("Error updating exam session:", updateError);
        }
        
        // Invalidate exam sessions cache
        queryClient.invalidateQueries({ queryKey: examSessionKeys.all });
      } catch (err) {
        console.error("Error saving exam results:", err);
      }
      
      // Record individual attempts for user progress tracking
      for (const [taskId, answer] of answers) {
        if (answer.isAutoGraded && answer.isCorrect !== undefined) {
          try {
            await recordAttemptMutation.mutateAsync({
              taskId,
              isCorrect: answer.isCorrect
            });
            completedTaskIds.push(taskId);
          } catch (error) {
            console.error("Error recording attempt:", error);
          }
        }
      }
      
      // Update TaskContext with completed tasks so panel shows progress
      if (completedTaskIds.length > 0) {
        markTasksCompleted(completedTaskIds);
      }
    } else if (user) {
      // No session (guest started and logged in later?) - just record attempts
      for (const [taskId, answer] of answers) {
        if (answer.isAutoGraded && answer.isCorrect !== undefined) {
          try {
            await recordAttemptMutation.mutateAsync({
              taskId,
              isCorrect: answer.isCorrect
            });
            completedTaskIds.push(taskId);
          } catch (error) {
            console.error("Error recording attempt:", error);
          }
        }
      }
      
      if (completedTaskIds.length > 0) {
        markTasksCompleted(completedTaskIds);
      }
    }

    // Update pending challenge with results if exists
    if (pendingChallengeId) {
      try {
        const timeSpentSecs = initialTimeSeconds - timeRemaining;
        const hrs = Math.floor(timeSpentSecs / 3600);
        const mins = Math.floor((timeSpentSecs % 3600) / 60);
        const timeFormatted = hrs > 0 ? `${hrs}h ${mins}min` : `${mins} min`;

        // Calculate totals for challenge update
        const allResults = examTasks.map(task => {
          const answer = answers.get(task.id);
          return {
            earnedPoints: answer?.earnedPoints ?? 0,
            maxPoints: task.maxPoints || (task.isTwoPoints ? 2 : 1),
          };
        });
        const challengeTotalEarned = allResults.reduce((s, r) => s + r.earnedPoints, 0);
        const challengeTotalMax = allResults.reduce((s, r) => s + r.maxPoints, 0);
        const challengePercentage = challengeTotalMax > 0 ? Math.round((challengeTotalEarned / challengeTotalMax) * 100) : 0;

        await supabase
          .from('challenges')
          .update({
            challenger_score: challengeTotalEarned,
            challenger_max_score: challengeTotalMax,
            challenger_percentage: challengePercentage,
            challenger_time_formatted: timeFormatted,
            is_pending: false,
          } as any)
          .eq('id', pendingChallengeId);

        setPendingChallengeId(null);
      } catch (err) {
        console.error("Error updating pending challenge:", err);
      }
    }
  }, [user, answers, examTasks, timeRemaining, initialTimeSeconds, currentSessionId, recordAttemptMutation, clearSavedExamState, markTasksCompleted, queryClient, pendingChallengeId, setPendingChallengeId]);
  
  // Reset exam mode (clears in-memory state but NOT localStorage - use forceReset for that)
  const resetExam = useCallback((forceReset: boolean = false) => {
    // Don't reset during initial restoration phase
    if (isRestoringState) return;
    
    setSelectedDocument(null);
    setExamTasks([]);
    setCurrentTaskIndex(0);
    setAnswers(new Map());
    setSkippedTaskIds([]);
    setTimeRemaining(EXAM_DURATION_SECONDS);
    setInitialTimeSeconds(EXAM_DURATION_SECONDS);
    setIsTimerRunning(false);
    setIsExamFinished(false);
    setCurrentSessionId(null);
    setChallengeData(null);
    setCustomExamData(null);
    warningShownRef.current = false;
    
    // Only clear localStorage when explicitly requested (e.g., after finishing exam)
    if (forceReset) {
      clearSavedExamState();
    }
  }, [clearSavedExamState, isRestoringState]);

  // Start a challenge exam with pre-loaded specific tasks
  const startChallengeExam = useCallback(async (tasks: Task[], doc: ExamDocument, data: ChallengeData) => {
    setChallengeData(data);
    setSelectedDocument(doc);
    setExamTasks(tasks);
    setCurrentTaskIndex(0);
    setAnswers(new Map());
    setSkippedTaskIds([]);
    setTimeRemaining(EXAM_DURATION_SECONDS);
    setInitialTimeSeconds(EXAM_DURATION_SECONDS);
    setIsTimerRunning(true);
    setIsExamFinished(false);
    setIsLoadingExam(false);

    // Create exam session in DB for authenticated users (challenge variant)
    let sessionId: string | null = null;
    if (user) {
      try {
        const { data: session, error: sessionError } = await supabase
          .from("exam_sessions")
          .insert({
            user_id: user.id,
            document_type: doc.documentType,
            year: doc.year,
            started_at: new Date().toISOString(),
            is_completed: false,
            challenge_id: data.id,
          } as any)
          .select()
          .single();

        if (sessionError) {
          console.error("Error creating challenge exam session:", sessionError);
        } else {
          sessionId = session.id;
        }
      } catch (err) {
        console.error("Error creating challenge exam session:", err);
      }
    }
    setCurrentSessionId(sessionId);
  }, [user]);

  // Start a custom exam from homework assignment
  const startCustomExam = useCallback(async (tasks: Task[], timeLimitMinutes: number, homeworkId: string) => {
    const customData: CustomExamData = { homeworkId, timeLimitMinutes };
    setCustomExamData(customData);
    warningShownRef.current = false;
    
    const doc: ExamDocument = { documentType: 'custom_exam', year: 0, taskCount: tasks.length };
    setSelectedDocument(doc);
    setExamTasks(tasks);
    setCurrentTaskIndex(0);
    setAnswers(new Map());
    setSkippedTaskIds([]);
    setTimeRemaining(timeLimitMinutes * 60);
    setInitialTimeSeconds(timeLimitMinutes * 60);
    setIsTimerRunning(true);
    setIsExamFinished(false);
    setIsLoadingExam(false);
    setChallengeData(null);

    // Create exam session in DB
    let sessionId: string | null = null;
    if (user) {
      try {
        const { data: session, error: sessionError } = await supabase
          .from("exam_sessions")
          .insert({
            user_id: user.id,
            document_type: 'custom_exam',
            year: 0,
            started_at: new Date().toISOString(),
            is_completed: false,
            homework_id: homeworkId,
          } as any)
          .select()
          .single();

        if (sessionError) {
          console.error("Error creating custom exam session:", sessionError);
        } else {
          sessionId = session.id;
        }
      } catch (err) {
        console.error("Error creating custom exam session:", err);
      }
    }
    setCurrentSessionId(sessionId);
  }, [user]);
  
  // Auto-finish exam when timer reaches 0 (BUG FIX: previously only set UI state, didn't save results)
  const finishExamRef = useRef(finishExam);
  finishExamRef.current = finishExam;
  
  useEffect(() => {
    if (timeRemaining === 0 && !isExamFinished && examTasks.length > 0 && selectedDocument) {
      finishExamRef.current();
    }
  }, [timeRemaining, isExamFinished, examTasks.length, selectedDocument]);

  // Load documents when entering exam mode
  useEffect(() => {
    if (isExamMode && availableDocuments.length === 0) {
      loadAvailableDocuments();
    }
  }, [isExamMode, availableDocuments.length, loadAvailableDocuments]);
  
  // Cleanup on exit exam mode - but not during restoration
  useEffect(() => {
    // Don't run cleanup during initial restoration phase
    if (isRestoringState) return;
    
    if (!isExamMode) {
      resetExam();
    }
  }, [isExamMode, resetExam, isRestoringState]);
  
  const value: ExamModeContextType = {
    isExamMode,
    selectedDocument,
    availableDocuments,
    examTasks,
    currentTaskIndex,
    currentTask,
    answers,
    skippedTaskIds,
    timeRemaining,
    isTimerRunning,
    isTimerVisible,
    isExamFinished,
    isLoadingExam,
    isLoadingDocuments,
    challengeData,
    pendingChallengeId,
    customExamData,
    completedCount,
    totalCount,
    progressPercentage,
    autoGradedResults,
    allTasksComplete,
    setIsExamMode,
    loadAvailableDocuments,
    selectDocument,
    startChallengeExam,
    startCustomExam,
    goToTask,
    goToNextTask,
    skipCurrentTask,
    recordAnswer,
    getAnswer,
    toggleTimer,
    toggleTimerRunning,
    finishExam,
    resetExam,
    setPendingChallengeId,
  };
  
  return (
    <ExamModeContext.Provider value={value}>
      {children}
    </ExamModeContext.Provider>
  );
};

export function useExamMode() {
  const context = useContext(ExamModeContext);
  if (!context) {
    throw new Error("useExamMode must be used within ExamModeProvider");
  }
  return context;
}
